// TEKLİF TOPLAM/KDV HESABI — TEK KAYNAK (JS)
// QuoteBuilderPage.jsx'ten çıkarıldı (30.10.2026). Python ikizi:
// backend/services/quote_totals.py — test_quote_totals_parity.py ile JS↔Python
// paritesi SABİTTİR; sohbet araçları (update_quote_items / revise_quote) aynı
// hesabı kullanır, sonuç ekrandan farklı çıkmaz.
//
// KURALLAR (birebir korunur):
// - MOTOR KDV AYRIMI (29.10.2026): motor bedeli (motorTotalInCur) daima %20 KDV
//   ile hesaplanır; kalan perde bedeli kalem oranından (örn. %10) gider;
//   Σ net değişmez. Motor bedeli kalem toplamını aşamaz.
// - O6: total = subtotal + KDV birebir. Manuel toplam/KDV verildiğinde ara toplam
//   KDV düşülerek türetilir (föyde "ara toplam + KDV ≠ toplam" çelişkisi olmaz).
// - vatExempt=true ise KDV ve kırılım HER ZAMAN sıfır/boş (manuel override bile olsa).
// - Kırılım (ara toplam, KDV) çiftine orantılı ölçeklenir; son satır yuvarlama
//   farkını kapatır → Σnet = ara toplam, Σkdv = KDV (birebir).
import { convertPrice } from "../appUtils.js";

const r2 = (x) => Math.round(x * 100) / 100;

// MANUEL BİRİM FİYAT → KALEM SATIR TOPLAMI (kalemın kendi biriminde)
// Kullanıcının girdiği birim fiyat GÖRÜNEN birimde KUTSALDIR (31.10.2026):
// kendi birimine (örn. EUR) çevirirken kuruşa YUVARLAMA YAPILMAZ. Yuvarlanırsa
// ekran geri dönüşte küsurat artığı gösterir (1300 ₺/m² → 1300,02 ₺/m²).
// Kanıt/pin: tests/test_manual_price_roundtrip.py (5 gerçek ekran vakası).
export function manualLineTotalFromUnit(item, unitPrice, fromCur, rates) {
  const isM2 = item.pricingMode !== "matrix" && Number(item.computedM2) > 0;
  const totDisp = isM2
    ? unitPrice * Number(item.computedM2) * Math.max(1, Number(item.qty) || 1)
    : unitPrice * (Number(item.qty) || 1);
  return convertPrice(totDisp, fromCur, item.currency, rates, "revenue");
}

export function computeQuoteTotals(items, opts = {}) {
  const {
    rates = {},
    targetCur = "TL",
    vatExempt = false,
    manualVat = null,
    manualTotal = null,
  } = opts;

  const itemsInTarget = items.map((it) => ({
    ...it,
    _tl: convertPrice(it.lineTotal, it.currency, targetCur, rates, "revenue"),
    _kdvRate: it.kdvRate ?? 20,
  }));
  const itemsSubtotal = itemsInTarget.reduce((acc, it) => acc + it._tl, 0);

  const kdvGroups = {};
  if (!vatExempt) {
    itemsInTarget.forEach((it) => {
      let motorTl = (!it.isAccessory && Number(it.motorTotalInCur) > 0)
        ? convertPrice(Number(it.motorTotalInCur), it.currency, targetCur, rates, "revenue")
        : 0;
      if (motorTl > it._tl) motorTl = it._tl;
      const baseTl = it._tl - motorTl;
      const rate = it._kdvRate;
      if (!kdvGroups[rate]) kdvGroups[rate] = { net: 0, kdv: 0 };
      kdvGroups[rate].net += baseTl;
      kdvGroups[rate].kdv += baseTl * (rate / 100);
      if (motorTl > 0) {
        if (!kdvGroups[20]) kdvGroups[20] = { net: 0, kdv: 0 };
        kdvGroups[20].net += motorTl;
        kdvGroups[20].kdv += motorTl * 0.20;
      }
    });
  }

  const autoVat = Object.values(kdvGroups).reduce((s, g) => s + g.kdv, 0);
  const grossTotal = itemsSubtotal + autoVat;

  // Admin: efektif KDV ve toplam (manuel override öncelikli) — O6
  const vatAmount = vatExempt ? 0 : (manualVat != null ? manualVat : autoVat);
  const total = vatExempt ? itemsSubtotal : (manualTotal != null ? manualTotal : (manualVat != null ? itemsSubtotal + vatAmount : grossTotal));
  const subtotal = vatExempt
    ? itemsSubtotal
    : ((manualTotal != null || manualVat != null) ? r2(Math.max(0, total - vatAmount)) : itemsSubtotal);

  const vatBreakdown = (() => {
    if (vatExempt) return [];
    const entries = Object.entries(kdvGroups).map(([rate, g]) => ({ rate: Number(rate), net: g.net, kdv: g.kdv }));
    if (!entries.length) {
      return vatAmount > 0 ? [{ rate: 20, net: subtotal, kdv: vatAmount }] : [];
    }
    const netScale = itemsSubtotal > 0 ? subtotal / itemsSubtotal : 1;
    const vatScale = autoVat > 0 ? vatAmount / autoVat : 1;
    const scaled = entries.map((e) => ({
      rate: e.rate,
      net: r2(e.net * netScale),
      kdv: r2(e.kdv * vatScale),
    }));
    const netSum = scaled.reduce((s, e) => s + e.net, 0);
    const vatSum = scaled.reduce((s, e) => s + e.kdv, 0);
    scaled[scaled.length - 1].net = r2(scaled[scaled.length - 1].net + (subtotal - netSum));
    scaled[scaled.length - 1].kdv = r2(scaled[scaled.length - 1].kdv + (vatAmount - vatSum));
    return scaled;
  })();

  return {
    itemsSubtotal, // ham Σ (eski satır 1012)
    grossTotal,    // ham toplam = itemsSubtotal + autoVat (eski satır 1035)
    autoVat,       // otomatik KDV (eski autoVatAmt)
    kdvGroups,     // ham kırılım grupları {rate: {net, kdv}} (ekran + 2320. satır kaydı)
    subtotal,      // efektif ara toplam (eski effectiveSubtotalQB)
    total,         // efektif toplam (eski effectiveTotalQB)
    vatAmount,     // efektif KDV (eski effectiveKdvQB)
    vatBreakdown,  // ölçeklenmiş kırılım (eski effectiveVatBreakdownQB)
  };
}
